import { useState, useEffect } from 'react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { AppBar, Box, Button, IconButton, Toolbar, Tooltip } from '@material-ui/core';
import type { Theme } from '@material-ui/core';
import { ChevronDown as ChevronDownIcon } from '../icons/chevron-down';
import { useSettings } from '../contexts/settings-context';
import { Moon as MoonIcon } from '../icons/moon';
import { Sun as SunIcon } from '../icons/sun';
import { DashboardNavbarMenu } from './dashboard-navbar-menu';
import { PendingInvitationsPopover } from './pending-invitations-popover';
import { QuickMenuPopover } from './quick-menu-popover';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
// import { faCircleQuestion, faCircleInfo, faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import { faU, faM, faS, faF, faBrain, faBookOpen, faA, faCompass } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { startOnboardingTour } from './onboarding/onboarding-tour-host';
import { TopbarIpopPills } from './topbar-ipop-pills';

import { useDispatch, useSelector } from "react-redux";
import { RootState } from '../store';

import { openUnknownDialog, closeUnknownDialog } from '../slices/unknownSlice';
import { unknownGetAll } from '../api/unknown';
import { openStakeholderDialog, closeStakeholderDialog } from '../slices/stakeholderSlice';
import { openFactorDialog, closeFactorDialog } from '../slices/factorSlice';
import { openMoveDialog, closeMoveDialog } from '../slices/moveSlice';
import { openMoveDialogSimpleCosting, closeMoveDialogSimpleCosting } from '../slices/moveSliceSimpleCosting';
import { openIntellectualPropertyDialog, closeIntellectualPropertyDialog } from '../slices/intellectualPropertySlice';
import { openBugReportDialog, closeBugReportDialog } from '../slices/bugReportSlice';

import { FactorDialog } from '../components/quick-actions/factor-dialog';
import { MoveDialog } from '../components/quick-actions/move-dialog';
import { MoveDialogSimpleCosting } from '../components/quick-actions/move-dialog-simple-costing';
import { UnknownDialog } from '../components/quick-actions/unknown-dialog';
import { StakeholderDialog } from '../components/quick-actions/stakeholder/stakeholder-dialog';
import { IntellectualPropertyDialog } from '../components/quick-actions/intellectual-property-dialog';
import { WelcomeDialog } from '../components/dialogs/welcome-dialog';
import { BugReportDialog } from '../components/quick-actions/bug-report-dialog';

const organizations = [
  {
    id: '6039124832',
    name: 'ACME LTD.'
  },
  {
    id: '3828312374',
    name: 'Division LTD.'
  }
];

export const DashboardNavbar: FC = () => {
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const { i18n, t } = useTranslation();
  const { settings, saveSettings } = useSettings();
  const [openMenu, setOpenMenu] = useState(false);
  const [darkMode, setDarkMode] = useState(settings.theme === 'dark');
  const [rtlDirection, setRtlDirection] = useState(settings.direction === 'rtl');
  const [currentOrganization, setCurrentOrganization] = useState(organizations[0]);
  const [showInfo, setShowInfo] = useState(false);

  const dispatch = useDispatch();
  const [unknownsTotal, setUnknownsTotal] = useState(0);

  const { unknownDialogOpen, unknownStatus } = useSelector((state: RootState) => state.unknown);
  const { stakeholderDialogOpen } = useSelector((state: RootState) => state.stakeholder);
  const { factorDialogOpen } = useSelector((state: RootState) => state.factor);
  const { moveDialogOpen } = useSelector((state: RootState) => state.move);
  const { moveDialogOpenSC } = useSelector((state: RootState) => state.moveSimpleCosting);
  const { intellectualPropertyDialogOpen } = useSelector((state: RootState) => state.intellectualProperty);
  const { bugReportDialogOpen } = useSelector((state: RootState) => state.bugReport);
  // const [unknownDialogOpen, setUnknownDialogOpen] = useState(false);

  useEffect(() => {
    const fetchUnknownsCount = async () => {
      try {
        const res = await unknownGetAll('description', 'asc', '', 1, 1);
        if (res.total !== undefined) {
          setUnknownsTotal(res.total);
        }
      } catch (error) {
        console.error('Error fetching unknowns count:', error);
      }
    };
    fetchUnknownsCount();
  }, []);

  // Update count when dialog closes after successful creation
  useEffect(() => {
    if (!unknownDialogOpen && unknownStatus === 'success') {
      const fetchUnknownsCount = async () => {
        try {
          const res = await unknownGetAll('description', 'asc', '', 1, 1);
          if (res.total !== undefined) {
            setUnknownsTotal(res.total);
          }
        } catch (error) {
          console.error('Error fetching unknowns count:', error);
        }
      };
      fetchUnknownsCount();
    }
  }, [unknownDialogOpen, unknownStatus]);

  const handleLanguageChange = (language: 'en' | 'fr'): void => {
    i18n.changeLanguage(language);
    saveSettings({
      ...settings,
      language
    });
    toast.success(t('toast_language_changed'));
  };

  const handleSwitchTheme = (): void => {
    saveSettings({
      ...settings,
      theme: settings.theme === 'light' ? 'dark' : 'light'
    });

    setDarkMode(settings.theme === 'light');
  };

  const handleSwitchDirection = (): void => {
    saveSettings({
      ...settings,
      direction: settings.direction === 'ltr' ? 'rtl' : 'ltr'
    });

    setRtlDirection(settings.direction === 'rtl');
  };

  const handleOrganizationChange = (organizationId: string): void => {
    const newOrganization = organizations.find(
      (organization) => organization.id === organizationId
    );

    if (!newOrganization) {
      return;
    }

    setCurrentOrganization(newOrganization);
  };

  return (
    <AppBar
      elevation={0}
      sx={{ backgroundColor: 'background.paper' }}
    >
      <Toolbar
        disableGutters
        sx={{
          alignItems: 'center',
          display: 'flex',
          height: 90,
          width: {
            xs: '100%',
            md: 'calc(100% - 250px)'
          },
          left: {
            xs: 0,
            md: 250
          },
          px: 3,
          py: 1
        }}
      >
        {/* <Box
          component={RouterLink}
          to="/projects"
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <img src='./static/ipop-logo.png' width={100} />
        </Box>
        <Divider
          flexItem
          orientation="vertical"
          sx={{
            borderColor: 'rgba(255,255,255,0.1)',
            mx: 3
          }}
        /> */}
        {/* Breadcrumb removed; quick actions moved to the left. */}
        <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center' }}>
          <TopbarIpopPills />
        </Box>
        <Box sx={{ flexGrow: 1 }} />

        <DashboardNavbarMenu
          onClose={() => setOpenMenu(false)}
          open={mdDown && openMenu}
        />
        <Button
          endIcon={(
            <ChevronDownIcon
              fontSize="small"
              sx={{
                ml: 0.5,
                transition: 'transform 250ms',
                transform: openMenu ? 'rotate(180deg)' : 'none'
              }}
            />
          )}
          onClick={() => setOpenMenu(true)}
          sx={{
            color: 'primary.contrastText',
            display: {
              md: 'none',
              xs: 'flex'
            }
          }}
          variant="text"
        >
          Menu
        </Button>
        {/* <LanguagePopover
          language={i18n.language}
          onLanguageChange={handleLanguageChange}
          sx={{
            display: {
              md: 'inline-flex',
              xs: 'none'
            }
          }}
        /> */}
        {/* <IconButton
          color="inherit"
          onClick={() => setShowInfo(!showInfo)}
          sx={{
            mx: 0.5,
            display: {
              md: 'inline-flex',
              xs: 'none'
            },
            color: 'primary.main'
          }}
        >
          <FontAwesomeIcon icon={faCircleInfo as IconProp} size="xs" />
        </IconButton> */}
        <Tooltip title="Replay guided tour" placement={'bottom'} arrow>
          <IconButton
            aria-label="Replay guided tour"
            onClick={() => startOnboardingTour()}
            sx={{ mx: 0.5, color: 'primary.main' }}
          >
            <FontAwesomeIcon icon={faCompass as IconProp} size="xs" />
          </IconButton>
        </Tooltip>
        <Tooltip title="IpOp Tools Guide" placement={'bottom'} arrow>
          <IconButton
            onClick={() => setShowInfo(!showInfo)}
            sx={{
              mx: 0.5,
              mr: 2,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              boxShadow: 7,
              fontSize: '18px',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                transform: 'translateY(-2px)',
              },
              '&:active': {
                transform: 'translateY(0px)',
                boxShadow: '0 2px 10px rgba(102, 126, 234, 0.4)',
              }
            }}
          >
            <FontAwesomeIcon icon={faBookOpen as IconProp} size="xs" />
          </IconButton>
        </Tooltip>
        {/* <IconButton
          color="inherit"
          onClick={() => dispatch(openBugReportDialog({element: {}, isEditabled: true, isDuplicated: false}))}
          sx={{
            mx: 0.5,
            display: {
              md: 'inline-flex',
              xs: 'none'
            },
            color: 'primary.main'
          }}
        >
          <FontAwesomeIcon icon={faCircleExclamation as IconProp} size="xs" />
        </IconButton> */}
        {/* <IconButton
          color="inherit"
          onClick={handleSwitchTheme}
          sx={{
            mx: 0.5,
            display: {
              md: 'inline-flex',
              xs: 'none'
            },
            color: 'primary.main'
          }}
        >
          {
            darkMode
              ? <FontAwesomeIcon icon={faSun as IconProp} size="xs" />
              : <FontAwesomeIcon icon={faMoonStars as IconProp} size="xs" />
          }
        </IconButton> */}
        <PendingInvitationsPopover sx={{ mr: 2 }} />
        <QuickMenuPopover
          currentOrganization={currentOrganization}
          darkMode={darkMode}
          onLanguageChange={handleLanguageChange}
          onOrganizationChange={handleOrganizationChange}
          onSwitchDirection={handleSwitchDirection}
          onSwitchTheme={handleSwitchTheme}
          organizations={organizations}
          rtlDirection={rtlDirection}
        />
        <UnknownDialog open={unknownDialogOpen} onClose={() => dispatch(closeUnknownDialog())} />
        <StakeholderDialog open={stakeholderDialogOpen} onClose={() => dispatch(closeStakeholderDialog())} />
        <FactorDialog open={factorDialogOpen} onClose={() => dispatch(closeFactorDialog())} />
        <MoveDialog open={moveDialogOpen} onClose={() => dispatch(closeMoveDialog())} stacked={unknownDialogOpen} />
        <MoveDialogSimpleCosting open={moveDialogOpenSC} onClose={() => dispatch(closeMoveDialogSimpleCosting())} />
        <IntellectualPropertyDialog open={intellectualPropertyDialogOpen} onClose={() => dispatch(closeIntellectualPropertyDialog())} />
        <WelcomeDialog open={showInfo} onClose={() => setShowInfo(false)} />
        {/* <BugReportDialog open={bugReportDialogOpen} onClose={() => dispatch(closeBugReportDialog())} /> */}
      </Toolbar>
    </AppBar>
  );
};
